import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, X, Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const MAPBOX_TOKEN = import.meta.env["VITE_MAPBOX_TOKEN"] ?? "pk.eyJ1IjoiYXlyc2hpcmVodWIiLCJhIjoiY21zYzg4ODJjMWE0dzJ4cXlya2cwN3RveSJ9.bngzyet7DaKyEQL6UEUNxg";

function newSessionToken() {
  return `${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

interface Suggestion {
  label: string;
  postcode: string;
  mapboxId: string;
}

interface Props {
  value: string;
  onSelect: (address: string, postcode: string, lat?: number, lng?: number) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}

function ManualAddressEntry({ onSelect, onBack }: { onSelect: (address: string, postcode: string) => void; onBack: () => void }) {
  const [line1, setLine1] = useState("");
  const [town, setTown] = useState("");
  const [postcode, setPostcode] = useState("");

  useEffect(() => {
    if (line1.trim() && town.trim() && postcode.trim()) {
      onSelect(`${line1.trim()}, ${town.trim()}, ${postcode.trim().toUpperCase()}`, postcode.trim().toUpperCase());
    } else {
      onSelect("", "");
    }
  }, [line1, town, postcode, onSelect]);

  const inputClass = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={line1}
        onChange={e => setLine1(e.target.value)}
        placeholder="Address line 1"
        autoComplete="address-line1"
        className={inputClass}
      />
      <input
        type="text"
        value={town}
        onChange={e => setTown(e.target.value)}
        placeholder="Town / City"
        autoComplete="address-level2"
        className={inputClass}
      />
      <input
        type="text"
        value={postcode}
        onChange={e => setPostcode(e.target.value)}
        placeholder="Postcode"
        autoComplete="postal-code"
        className={inputClass}
      />
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-blue-600 hover:text-blue-800 underline"
      >
        ← Back to address search
      </button>
    </div>
  );
}

export function AddressAutocomplete({ value, onSelect, placeholder = "Start typing an address…", className, inputClassName }: Props) {
  const [text, setText] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockedRef = useRef(false);
  const prevRef = useRef(value);
  const sessionTokenRef = useRef(newSessionToken());
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only sync from an EXTERNAL, non-empty value (a saved address, "use my
    // location", or a selection). Ignore empty-string updates — those come from
    // the component's own onSelect("","") when the user starts editing; re-syncing
    // them here would re-lock the field and wipe what's being typed, so suggestions
    // never appear (the address-autofill-while-typing bug).
    if (value && value !== prevRef.current) {
      prevRef.current = value;
      lockedRef.current = true;
      setText(value);
      setSuggestions([]);
      setOpen(false);
    } else if (!value) {
      prevRef.current = value;
    }
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = useCallback((q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => {
      if (lockedRef.current) return;
      setLoading(true);
      const url = `https://api.mapbox.com/search/searchbox/v1/suggest`
        + `?q=${encodeURIComponent(q)}`
        + `&access_token=${MAPBOX_TOKEN}`
        + `&session_token=${sessionTokenRef.current}`
        + `&country=gb&language=en&limit=6&types=poi,address&proximity=-4.5,55.6`;
      fetch(url)
        .then(r => r.ok ? r.json() as Promise<{ suggestions: Array<{ name: string; mapbox_id: string; full_address?: string; place_formatted?: string; context?: { postcode?: { name: string } } }> }> : null)
        .then(data => {
          if (!lockedRef.current && data) {
            const results = data.suggestions.map(s => ({
              label: (s.full_address ?? `${s.name}, ${s.place_formatted ?? ""}`).replace(", United Kingdom", "").replace(/,\s*[^,]*\bcouncil\b\s*(?=,|$)/gi, ""),
              postcode: s.context?.postcode?.name ?? "",
              mapboxId: s.mapbox_id,
            }));
            setSuggestions(results);
            setOpen(results.length > 0);
          }
        })
        .catch(() => null)
        .finally(() => setLoading(false));
    }, 350);
  }, []);

  const handleChange = (v: string) => {
    if (lockedRef.current) {
      lockedRef.current = false;
      onSelect("", "");
    }
    setText(v);
    search(v);
  };

  const handleSelect = async (s: Suggestion) => {
    lockedRef.current = true;
    prevRef.current = s.label;
    setText(s.label);
    setSuggestions([]);
    setOpen(false);
    let lat: number | undefined;
    let lng: number | undefined;
    try {
      const res = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${s.mapboxId}`
        + `?access_token=${MAPBOX_TOKEN}&session_token=${sessionTokenRef.current}`
      );
      if (res.ok) {
        const data = await res.json() as { features: Array<{ geometry: { coordinates: [number, number] } }> };
        const f = data.features[0];
        if (f) { lat = f.geometry.coordinates[1]; lng = f.geometry.coordinates[0]; }
      }
    } catch { /* ignore */ }
    sessionTokenRef.current = newSessionToken();
    onSelect(s.label, s.postcode, lat, lng);
  };

  const handleClear = () => {
    lockedRef.current = false;
    prevRef.current = "";
    setText("");
    setSuggestions([]);
    setOpen(false);
    sessionTokenRef.current = newSessionToken();
    onSelect("", "");
  };

  const enterManual = () => {
    handleClear();
    setManualMode(true);
  };

  const exitManual = () => {
    setManualMode(false);
    onSelect("", "");
  };

  if (manualMode) {
    return (
      <div className={cn(className)}>
        <ManualAddressEntry onSelect={onSelect} onBack={exitManual} />
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={enterManual}
        className="block text-xs text-blue-600 hover:text-blue-800 underline mb-1.5"
      >
        Can't find your address? Enter manually
      </button>
      <div className="relative flex items-center">
        <Search className="absolute left-3 h-4 w-4 text-slate-400 pointer-events-none shrink-0" />
        <input
          type="text"
          value={text}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors",
            "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            "pl-9 pr-8",
            inputClassName,
          )}
        />
        <div className="absolute right-2 flex items-center">
          {loading
            ? <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
            : text.length > 0
              ? <button type="button" onClick={handleClear} className="text-slate-400 hover:text-slate-600 transition-colors">
                  <X className="h-4 w-4" />
                </button>
              : null
          }
        </div>
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={e => { e.preventDefault(); void handleSelect(s); }}
              className="w-full flex items-start gap-2.5 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-b-0"
            >
              <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-slate-800 leading-snug">{s.label}</p>
                {s.postcode && <p className="text-xs text-slate-400 mt-0.5">{s.postcode}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
